import ProductCard from '../../components/ProductCard'
import React, { useState, useEffect, useContext } from 'react'
import { InferGetServerSidePropsType } from 'next'
import SocialShare from '../../components/SocialShare'
import Context from '../../context'
import getConfig from 'next/config'
import Head from 'next/head'
import GeneralImageHero from '../../components/GeneralImageHero'
import Image from 'next/image'
import { useAuthAPI } from '../../hooks/api.hooks'
import { useAddToCart } from '../../hooks/cart.hooks'
import { defaultLoader } from '../../utils/custom-image-loader'
import SVG from '../../components/SVG'
import { writeCookie } from '../../utils/writeCookie'
import { useIonModal } from '@ionic/react'
import { formatF1String, setPageViewData } from '../../utils/analytics'

const { publicRuntimeConfig } = getConfig()

const ArticlePage = ({
  article,
  fullUrl,
  slug
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { state } = useContext(Context)
  const [total, setTotal] = useState(0)
  const [mobileImg, setMobileImage] = useState('')
  const [animated, setAnimated] = useState(false)
  const authAPICall = useAuthAPI()
  const addToCartCall = useAddToCart()
  let addAllUPCList = ''

  const [products, setProducts] = useState(
    article[0].template.find((element) => element.type === 'products')
  )

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.br_data = window.br_data || {}
      window.br_data.acct_id = publicRuntimeConfig.BR_ID
      window.br_data.ptype = 'content'
      window.br_data.test_data = publicRuntimeConfig.BR_TEST_DATA
      window.br_data.user_id = state.user.UID || state.deviceId
      window.br_data.view_id = state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE;

      (() => {
        const brtrk = document.createElement('script')
        brtrk.type = 'text/javascript'
        brtrk.async = true
        brtrk.src = `//cdn.brcdn.com/v1/br-trk-${publicRuntimeConfig.BR_ID
          }.js?v=${Date.now()}`
        const s = document.getElementsByTagName('script')[0]
        s.parentNode.insertBefore(brtrk, s)
      })()
    }
  }, [])

  useEffect(() => {
    authAPICall(
      'collections',
      'article',
      'GET',
      {
        slug,
        popkey: publicRuntimeConfig.POP_KEY,
        store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE
      },
      {}
    ).then((data) =>
      setProducts(
        data[0].template.find((element) => element.type === 'products')
      )
    )
  }, [state.cart.store?.storenumber])

  useEffect(() => {
    setMobileImage(article[0].mobile_image)
  }, [])

  useEffect(() => {
    const pageName = `article|${formatF1String(article[0].title)}`
    const title = formatF1String(article[0].title)
    setPageViewData(pageName, 'Other', title)
  }, [])

  const renderProducts = () => {
    let totalPrice = 0

    if (!products) return []
    const arr = products.data.map((product) => {
      if (product.in_stock && !product.in_store_only && parseFloat(product.final_price || 0)?.toFixed(2) !== '0.01') {
        totalPrice += parseFloat(product.final_price)
      }

      return (
        <div key={product.slug} className="article__content-products-card">
          <ProductCard data={product} vertical={true} forceVertical={true} />
        </div>
      )
    })
    totalPrice !== total && setTotal(totalPrice)

    for (const product of products.data) {
      if (product.in_stock && !product.in_store_only) {
        if (addAllUPCList && addAllUPCList.length > 0) {
          addAllUPCList += ','
        }
        addAllUPCList += product.upc
      }
    }

    return arr
  }

  const handleDismiss = () => {
    dismiss()
  }
  const [present, dismiss] = useIonModal(SocialShare, {
    handleDismiss,
    pageType: 'Article',
    item: article[0],
    image: article[0].image_urls,
    title: article[0].title,
    description: article[0].description
  })

  const addAllClicked = async () => {
    if (!state.cart?.products?.length) {
      writeCookie('needToConfirmStore', true, 168)
    }
    for (const product of products.data) {
      if (product.in_stock && !product.in_store_only) {
        setAnimated(true)
        addToCartCall(state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE, product.upc, product.digital_sku, product.sku, 1, true).then(() => setAnimated(false))
      }
    }
  }

  const renderButtons = () => {
    return (
      <button
        id="article-add-all"
        data-price={total.toFixed(2)}
        data-upc={addAllUPCList}
        onClick={addAllClicked}
        aria-label="Add all to cart"
        disabled={
          !products?.data?.some(
            (product) =>
              product.in_stock && !product.in_store_only && parseFloat(product.final_price) > 0.01
          )
        }
        className={`btn__primary ${!animated && 'unchecked_all_cart'} ${animated && 'check_cart'}`}
      />
    )
  }

  const renderContent = () => {
    return article[0].template.map((article) => {
      switch (article.type) {
        case 'copy':
          return (
            <div
              dangerouslySetInnerHTML={{ __html: article.value }}
              className="rte article__content-content-block"
            />
          )
        case 'image':
          return (
            article.value && (
              <div className="image-next article__content-content-block">
                <Image
                  src={article.dynamicMediaUrl || article.value}
                  alt={products.slug}
                  layout="fill"
                  loader={defaultLoader}
                />
              </div>
            )
          )
        case 'video':
          return (
            <div className="article__content-content-block">
              <iframe width="420" height="315" src={article.value}></iframe>
            </div>
          )
        default:
          return <div></div>
      }
    })
  }

  return (
    <div className="article">
      <Head>
        <title>{article[0].title} </title>
        <meta name="description" content={`Checkout ${article[0].title} at pOpshelf today to learn more.`} />
        <meta property="og:url" content={fullUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={Object.keys(article).length !== 0 && article[0].title} />
        <meta property="og:description" content={Object.keys(article).length !== 0 && article[0].title} />
        <meta property="og:image" content={article.length && article[0].main_image} />
      </Head>

      <section className="article__wrapper">
        <div className='article__image-hero'>
          <GeneralImageHero image={article[0].main_image} dynamicMediaUrl={article[0].dynamicMediaUrl} dynamicMediaMobileUrl={article[0].dynamicMediaMobileUrl} mobileImage={mobileImg} />
        </div>

        <div className="article__container">
          <div className="article__title">
            <h1>{article[0]?.title} </h1>

            <button className="share" onClick={() => present()} aria-label="Open share modal">
              <SVG name="share" label="share" size="md" />
            </button>
          </div>

          <div className="article__content">
            <div className="article__content-content">{Object.keys(article).length !== 0 && renderContent()}</div>
            {article[0].template.find((element) => element.type === 'products') && (
              <div className="article__content-products" id={article[0].title}>
                <p className="article__content-products-total">
                  Total: <span>${total.toFixed(2)}</span>
                </p>
                {renderProducts()}
                {typeof window !== 'undefined' && renderButtons()}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

export default ArticlePage

export const getServerSideProps = async ({ query, req }) => {
  let fullUrl
  if (req) {
    fullUrl = req.headers.host + req.url
  } else {
    fullUrl =
      window.location.protocol +
      '//' +
      window.location.hostname +
      (window.location.port ? ':' + window.location.port : '')
  }
  const res = await fetch(
    `${publicRuntimeConfig.APP_ENDPOINT}/api/collections/${publicRuntimeConfig.ENV}/article?slug=${query.slug}&popkey=${publicRuntimeConfig.POP_KEY}&store=${publicRuntimeConfig.DEFAULT_STORE}`,
    {
      headers: {
        deviceid: ''
      }
    }
  )
  const article = await res.json()
  return {
    props: {
      article,
      fullUrl,
      slug: query.slug
    }
  }
}
